import { useState } from "react";
import type { Folder, Tag } from "@note-app/domain";
import {
  Button,
  ConfirmDialog,
  OperationStatus,
  Select,
  TextField,
} from "@note-app/ui";
import type {
  CatalogSnapshot,
  KnowledgeAppViewModel,
} from "../view-model.ts";

export interface CatalogManagerProps {
  readonly catalog: CatalogSnapshot;
  readonly viewModel: KnowledgeAppViewModel;
  readonly onChanged: () => void;
  readonly onOpenFolder: (folderId: string) => void;
  readonly onOpenTag: (tagId: string) => void;
  readonly onOpenFavorites: () => void;
  readonly onOpenTrash: () => void;
}

type EditTarget =
  | { readonly kind: "folder"; readonly value: Folder }
  | { readonly kind: "tag"; readonly value: Tag };

export function CatalogManager({
  catalog,
  viewModel,
  onChanged,
  onOpenFolder,
  onOpenTag,
  onOpenFavorites,
  onOpenTrash,
}: CatalogManagerProps) {
  const [folderName, setFolderName] = useState("");
  const [folderParentId, setFolderParentId] = useState("");
  const [tagName, setTagName] = useState("");
  const [tagColor, setTagColor] = useState("#8c4f36");
  const [editTarget, setEditTarget] = useState<EditTarget>();
  const [editName, setEditName] = useState("");
  const [editParentId, setEditParentId] = useState("");
  const [editColor, setEditColor] = useState("#8c4f36");
  const [deleteTarget, setDeleteTarget] = useState<EditTarget>();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();

  const run = async (operation: () => Promise<unknown>) => {
    if (busy) return false;
    setBusy(true);
    setError(undefined);
    try {
      await operation();
      onChanged();
      return true;
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "整理操作失败，请重试。");
      return false;
    } finally {
      setBusy(false);
    }
  };

  const startEditing = (target: EditTarget) => {
    setEditTarget(target);
    setEditName(target.value.name);
    if (target.kind === "folder") {
      setEditParentId(target.value.parentId ?? "");
    } else {
      setEditColor(target.value.color ?? "#8c4f36");
    }
  };

  return (
    <div className="knowledge-catalog">
      <section className="knowledge-collection-shortcuts" aria-label="常用集合">
        <button type="button" onClick={onOpenFavorites}>
          <span aria-hidden="true">★</span>
          <strong>收藏</strong>
          <small>集中查看标记的重要内容</small>
        </button>
        <button type="button" onClick={onOpenTrash}>
          <span aria-hidden="true">♲</span>
          <strong>回收站</strong>
          <small>恢复或永久删除笔记</small>
        </button>
      </section>

      {error ? (
        <OperationStatus
          status="error"
          title="整理操作没有完成"
          description={error}
        />
      ) : null}

      <section className="knowledge-settings-card">
        <div className="knowledge-section-heading">
          <div>
            <h2>文件夹</h2>
            <p>用层级文件夹建立稳定的内容结构。</p>
          </div>
        </div>
        <form
          className="knowledge-inline-form"
          onSubmit={(event) => {
            event.preventDefault();
            const name = folderName.trim();
            if (!name) return;
            void run(() =>
              viewModel.createFolder({
                name,
                ...(folderParentId ? { parentId: folderParentId } : {}),
              }),
            ).then((created) => {
              if (created) {
                setFolderName("");
                setFolderParentId("");
              }
            });
          }}
        >
          <TextField
            label="新文件夹名称"
            value={folderName}
            disabled={busy}
            maxLength={120}
            onChange={(event) => setFolderName(event.currentTarget.value)}
          />
          <Select
            label="上级文件夹"
            value={folderParentId}
            disabled={busy}
            onChange={(event) => setFolderParentId(event.currentTarget.value)}
          >
            <option value="">无（顶层）</option>
            {catalog.folders.map((folder) => (
              <option key={folder.id} value={folder.id}>{folder.name}</option>
            ))}
          </Select>
          <Button variant="primary" type="submit" disabled={busy || !folderName.trim()}>
            新建文件夹
          </Button>
        </form>
        <CatalogList
          empty="还没有文件夹。"
          items={catalog.folders.map((folder) => ({
            id: folder.id,
            name: folder.name,
            detail: `${catalog.itemCountByFolder.get(folder.id) ?? 0} 条内容`,
            onOpen: () => onOpenFolder(folder.id),
            onEdit: () => startEditing({ kind: "folder", value: folder }),
            onDelete: () => setDeleteTarget({ kind: "folder", value: folder }),
          }))}
          disabled={busy}
        />
      </section>

      <section className="knowledge-settings-card">
        <div className="knowledge-section-heading">
          <div>
            <h2>标签</h2>
            <p>标签可以横跨文件夹组合内容。</p>
          </div>
        </div>
        <form
          className="knowledge-inline-form knowledge-inline-form--tag"
          onSubmit={(event) => {
            event.preventDefault();
            const name = tagName.trim();
            if (!name) return;
            void run(() => viewModel.createTag({ name, color: tagColor })).then(
              (created) => {
                if (created) setTagName("");
              },
            );
          }}
        >
          <TextField
            label="新标签名称"
            value={tagName}
            disabled={busy}
            maxLength={120}
            onChange={(event) => setTagName(event.currentTarget.value)}
          />
          <ColorField label="标签颜色" value={tagColor} disabled={busy} onChange={setTagColor} />
          <Button variant="primary" type="submit" disabled={busy || !tagName.trim()}>
            新建标签
          </Button>
        </form>
        <CatalogList
          empty="还没有标签。"
          items={catalog.tags.map((tag) => ({
            id: tag.id,
            name: tag.name,
            detail: `${catalog.itemCountByTag.get(tag.id) ?? 0} 条内容`,
            ...(tag.color === undefined ? {} : { color: tag.color }),
            onOpen: () => onOpenTag(tag.id),
            onEdit: () => startEditing({ kind: "tag", value: tag }),
            onDelete: () => setDeleteTarget({ kind: "tag", value: tag }),
          }))}
          disabled={busy}
        />
      </section>

      {catalog.trashedFolders.length || catalog.trashedTags.length ? (
        <section className="knowledge-settings-card">
          <h2>已删除的分类</h2>
          <p>恢复分类不会改写笔记正文或画板内容。</p>
          <div className="knowledge-catalog-trash">
            {catalog.trashedFolders.map((folder) => (
              <div key={folder.id}>
                <span>文件夹 · {folder.name}</span>
                <Button
                  size="small"
                  variant="ghost"
                  disabled={busy}
                  onClick={() => void run(() => viewModel.restoreFolder(folder))}
                >
                  恢复
                </Button>
              </div>
            ))}
            {catalog.trashedTags.map((tag) => (
              <div key={tag.id}>
                <span>标签 · {tag.name}</span>
                <Button
                  size="small"
                  variant="ghost"
                  disabled={busy}
                  onClick={() => void run(() => viewModel.restoreTag(tag))}
                >
                  恢复
                </Button>
              </div>
            ))}
          </div>
        </section>
      ) : null}

      {editTarget ? (
        <section className="knowledge-settings-card" aria-label="编辑分类">
          <h2>编辑“{editTarget.value.name}”</h2>
          <form
            className="knowledge-inline-form"
            onSubmit={(event) => {
              event.preventDefault();
              const name = editName.trim();
              if (!name) return;
              const operation =
                editTarget.kind === "folder"
                  ? () =>
                      viewModel.updateFolder({
                        folder: editTarget.value,
                        name,
                        parentId: editParentId || null,
                      })
                  : () =>
                      viewModel.updateTag({
                        tag: editTarget.value,
                        name,
                        color: editColor,
                      });
              void run(operation).then((updated) => {
                if (updated) setEditTarget(undefined);
              });
            }}
          >
            <TextField
              label="名称"
              value={editName}
              disabled={busy}
              onChange={(event) => setEditName(event.currentTarget.value)}
            />
            {editTarget.kind === "folder" ? (
              <Select
                label="上级文件夹"
                value={editParentId}
                disabled={busy}
                onChange={(event) => setEditParentId(event.currentTarget.value)}
              >
                <option value="">无（顶层）</option>
                {catalog.folders
                  .filter((folder) => folder.id !== editTarget.value.id)
                  .map((folder) => (
                    <option key={folder.id} value={folder.id}>{folder.name}</option>
                  ))}
              </Select>
            ) : (
              <ColorField
                label="标签颜色"
                value={editColor}
                disabled={busy}
                onChange={setEditColor}
              />
            )}
            <div className="knowledge-dialog-actions">
              <Button disabled={busy} onClick={() => setEditTarget(undefined)}>取消</Button>
              <Button variant="primary" type="submit" disabled={busy || !editName.trim()}>
                保存
              </Button>
            </div>
          </form>
        </section>
      ) : null}

      <ConfirmDialog
        open={deleteTarget !== undefined}
        onOpenChange={(open) => {
          if (!open) setDeleteTarget(undefined);
        }}
        title={deleteTarget ? `删除“${deleteTarget.value.name}”？` : "删除分类？"}
        description="分类会进入可恢复区域，笔记和画板内容不会被永久删除。"
        confirmLabel="删除分类"
        cancelLabel="取消"
        destructive
        onConfirm={async () => {
          if (!deleteTarget) return;
          await (deleteTarget.kind === "folder"
            ? viewModel.trashFolder(deleteTarget.value)
            : viewModel.trashTag(deleteTarget.value));
          onChanged();
          setDeleteTarget(undefined);
        }}
      />
    </div>
  );
}

interface CatalogListItem {
  readonly id: string;
  readonly name: string;
  readonly detail: string;
  readonly color?: string;
  readonly onOpen: () => void;
  readonly onEdit: () => void;
  readonly onDelete: () => void;
}

function CatalogList({
  items,
  empty,
  disabled,
}: {
  readonly items: readonly CatalogListItem[];
  readonly empty: string;
  readonly disabled: boolean;
}) {
  if (!items.length) return <p>{empty}</p>;
  return (
    <div className="knowledge-catalog-list">
      {items.map((item) => (
        <article key={item.id}>
          <button type="button" disabled={disabled} onClick={item.onOpen}>
            {item.color ? (
              <span
                className="knowledge-tag-dot"
                style={{ background: item.color }}
                aria-hidden="true"
              />
            ) : null}
            <span><strong>{item.name}</strong><small>{item.detail}</small></span>
          </button>
          <div>
            <Button size="small" variant="ghost" disabled={disabled} onClick={item.onEdit}>编辑</Button>
            <Button size="small" variant="ghost" disabled={disabled} onClick={item.onDelete}>删除</Button>
          </div>
        </article>
      ))}
    </div>
  );
}

function ColorField({
  label,
  value,
  disabled,
  onChange,
}: {
  readonly label: string;
  readonly value: string;
  readonly disabled: boolean;
  readonly onChange: (value: string) => void;
}) {
  return (
    <label className="knowledge-color-field">
      <span>{label}</span>
      <input
        type="color"
        value={value}
        disabled={disabled}
        onChange={(event) => onChange(event.currentTarget.value)}
      />
      <code>{value}</code>
    </label>
  );
}
